import { createComparator } from '@tmapy/utils';
import { geometryTypeAndLayoutByWKT } from '../index.js';
import { getCreateRouteId, getListRouteId } from './utils.js';
export const buildLayerSwitcherConfig = (tinkerConfig, nonTranslatableResourceStrings) => {
    const mapLayerGroups = tinkerConfig.mapLayerGroups.edges
        .map((edge) => edge.node)
        .sort(createComparator((asc) => asc.order, (asc) => asc.nameId));
    const mapLayerGroupById = new Map();
    // dummy group only for children
    const rootGroup = {
        groupId: ``,
        order: 0,
        children: [],
    };
    // null => root group
    mapLayerGroupById.set(null, rootGroup);
    const groups = mapLayerGroups.reduce((groups, group) => {
        groups[group.nameId] = group;
        return groups;
    }, {});
    const areParentsDirect = (groupId) => {
        const parentIds = [];
        let parentId = groupId;
        while (parentId) {
            parentIds.push(parentId);
            if (!groups[parentId]) {
                throw new Error('BuildLayerSwitcherConfig - The parent layer group is missing from the configuration.');
            }
            parentId = groups[parentId]?.parent?.nameId;
            if (parentId && parentIds.includes(parentId))
                return false;
        }
        return true;
    };
    const areParentsInLoop = !mapLayerGroups.map((group) => group.nameId).every(areParentsDirect);
    if (areParentsInLoop) {
        throw new Error('BuildLayerSwitcherConfig - Parents of layer groups are in a loop.');
    }
    // register all groups
    for (const mapLayerGroup of mapLayerGroups) {
        mapLayerGroupById.set(mapLayerGroup.nameId, {
            groupId: `${mapLayerGroup.nameId}`,
            children: [],
            order: mapLayerGroup.order,
            isExclusive: mapLayerGroup.isExclusive,
            exclusiveLayers: mapLayerGroup.exclusiveLayers,
            isExpanded: mapLayerGroup.isExpanded,
        });
        nonTranslatableResourceStrings[`layerSwitcher.group.${mapLayerGroup.nameId}`] =
            mapLayerGroup.title;
    }
    // add groups to parents
    for (const mapLayerGroup of mapLayerGroups) {
        const parentId = mapLayerGroup.parent?.nameId ?? null;
        let parent = mapLayerGroupById.get(parentId);
        if (!parent) {
            console.warn(`Broken tie to parent from group ${mapLayerGroup.nameId} to ${parentId}`);
            parent = mapLayerGroupById.get(null);
        }
        const group = mapLayerGroupById.get(mapLayerGroup.nameId);
        parent.children.push(group);
    }
    // add layers to groups
    for (const mapLayerEdge of tinkerConfig.mapLayers.edges) {
        const mapLayer = mapLayerEdge.node;
        if (mapLayer.isInLayerSwitcher) {
            const layerGroup = mapLayer.mapLayerGroup?.nameId ?? null;
            const mapLayerGroup = mapLayerGroupById.get(layerGroup);
            if (!mapLayerGroup) {
                const mapLayerGroupId = mapLayer.mapLayerGroup?.nameId ?? layerGroup;
                throw new Error(`BuildLayerSwitcherConfig - Broken tie to group '${mapLayerGroupId}' for layer '${mapLayer.nameId}'.`);
            }
            const layerChildren = {
                layerId: mapLayer.nameId,
                order: mapLayer.order,
                isOpacitySliderVisible: mapLayer.isOpacitySliderVisible,
                routeId: getListRouteId(mapLayer.nameId),
                createRouteId: mapLayer.canInsert ? getCreateRouteId(mapLayer.nameId) : undefined,
                drawGeometryType: mapLayer.mapService.geometryType
                    ? geometryTypeAndLayoutByWKT[mapLayer.mapService.geometryType]
                        .geometryType
                    : undefined,
            };
            mapLayerGroup.children.push(layerChildren);
            nonTranslatableResourceStrings[`layer.${mapLayer.nameId}`] = mapLayer.title;
            // layer legend nonTranslatableResourceStrings
            if (mapLayer.mapBoxStyleLegend && Array.isArray(mapLayer.mapBoxStyleLegend)) {
                mapLayer.mapBoxStyleLegend.forEach((item) => {
                    nonTranslatableResourceStrings[`legend.${mapLayer.nameId}.${item.id}`] =
                        item.label || item.id;
                });
            }
        }
    }
    // sort group children by order
    for (const group of mapLayerGroupById.values()) {
        group.children.sort(createComparator((asc) => asc.order, (asc) => asc.zIndex));
    }
    const map = tinkerConfig.maps.edges[0]?.node;
    return {
        enableFilter: map?.enableLayerSwitcherFilter,
        enableCustomLayers: map?.enableLayerSwitcherCustomLayers,
        groups: rootGroup.children,
    };
};
//# sourceMappingURL=buildLayerSwitcherConfig.js.map